/**
 * What the lock screen card says (Stage 6, Sam's picks 2026-10-31; the native side draws it - ios/App/PriiLiveActivity).
 *
 * Now: your pick that is on right now. Next: your next pick, or for a show with no picks the show itself. A festival
 * with no picks has neither row, only the top line and the buttons. Set times are wall-clock times in the event's
 * zone (CLAUDE.md: showtimes belong in the venue's zone), and a set starting before 6 AM belongs to the night before,
 * as the Sets grid reads them. Only rows with a set time are used: the pipeline writes none below the 0.90 bar.
 *
 * Pure: the driver (LiveActivityDriver) gathers the inputs and sends the result to the plugin.
 */
import {
  DEFAULT_EVENT_TIMEZONE,
  zonedWallTimeToMs,
  getEventStartMs,
} from '../proximity/data/eventStart';
import { resolveEventDisplayName } from '../../utils/eventDisplayName';
import { voteKey, festivalDaysFromLineup } from '../../utils/lineupFormat';
import { ringColor } from '../map/faceStyle';
import { getEventImageUrl } from '../images/eventImageUrl';
import { getEventEndsAt } from '../../utils/eventCutoff';

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;
/** A set with no listed end: an hour, as the map's stage cards assume. */
const DEFAULT_SET_MS = HOUR_MS;
/** Sets starting before this hour belong to the previous performance_day's night. */
const LATE_NIGHT_CUTOFF_HOUR = 6;
const MAX_FACES = 4;

function wallMs(day, time, tz) {
  const [y, mo, d] = String(day).slice(0, 10).split('-').map(Number);
  const [h, mi, s = 0] = String(time).split(':').map(Number);
  const ms = zonedWallTimeToMs(y, mo, d, h, mi, s, tz);
  return Number.isFinite(ms) ? ms : null;
}

/** The lineup's timed sets as { row, startMs, endMs }, one per announced set (a b2b has a row per artist). */
export function timedSets(lineup, tz) {
  const out = [];
  const seen = new Set();
  for (const row of lineup ?? []) {
    if (!row?.performance_day || !row.set_start_time) continue;
    let startMs = wallMs(row.performance_day, row.set_start_time, tz);
    if (startMs == null) continue;
    if (Number(row.set_start_time.slice(0, 2)) < LATE_NIGHT_CUTOFF_HOUR) startMs += DAY_MS;
    let endMs = row.set_end_time ? wallMs(row.performance_day, row.set_end_time, tz) : null;
    if (endMs != null && endMs <= startMs) endMs += DAY_MS;
    const name = row.billing_name || row.artist_name;
    const key = `${name}|${startMs}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      row,
      name,
      startMs,
      endMs: endMs ?? startMs + DEFAULT_SET_MS,
      hasEnd: endMs != null,
    });
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}

/**
 * "35m" / "8h40" for the ring, "35 min" / "8 hr 40 min" for the island: whole minutes, rounded up. Within the hour the
 * card counts the minutes itself (Sam's pick C, 2026-11-01); these stand in over an hour, and on a card drawn before.
 */
function countdownLabels(startMs, now) {
  const mins = Math.max(0, Math.ceil((startMs - now) / 60000));
  if (mins < 60) return { ring: `${mins}m`, countdown: `${mins} min` };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return {
    ring: `${h}h${String(m).padStart(2, '0')}`,
    countdown: m ? `${h} hr ${m} min` : `${h} hr`,
  };
}

function setInfo(tag, s, stageFallback, imageFallback = null) {
  return {
    tag,
    name: s.name,
    stage: s.row.stage || stageFallback || '',
    start: s.startMs,
    end: s.hasEnd ? s.endMs : null,
    imageUrl: s.row.spotify_image_url || imageFallback,
    image: null,
  };
}

/** A show's picture: the headliner's photo, else the event's own artwork. */
function showImageUrl(event, lineup) {
  const acts = (lineup ?? []).filter((r) => r?.spotify_image_url);
  const top =
    acts.find((r) => String(r.role || '').toLowerCase() === 'headliner') ??
    [...acts].sort((a, b) => (a.billing_order ?? 1e9) - (b.billing_order ?? 1e9))[0];
  return top?.spotify_image_url || getEventImageUrl(event) || null;
}

/**
 * The card for `event` at `now`, or null when there is nothing to show (no event).
 *
 * @param {object} p
 * @param {object} p.event events row (+ venues) - the session's event
 * @param {string} p.rootEventId the festival the lineup and picks hang off (the event itself for a show)
 * @param {object[]} p.lineup event_lineup rows
 * @param {object[]} p.votes your user_artist_votes rows ({ artist_id, performance_day })
 * @param {number} p.here friends sharing at the event (Find's count)
 * @param {number} p.of friends Find watches for it
 * @param {{ displayName?: string, photoUrl?: string|null }[]} p.faces friends here, best first
 * @param {number} [p.now]
 */
export function buildCardState({
  event,
  rootEventId,
  lineup,
  votes,
  here,
  of,
  faces,
  now = Date.now(),
}) {
  if (!event?.event_id) return null;
  const eventId = String(event.event_id);
  const rootId = String(rootEventId || eventId);
  const tz = event.timezone || event.venues?.timezone || DEFAULT_EVENT_TIMEZONE;
  const festival = Boolean(event.is_festival || event.multi_day);
  const venueName = event.venues?.name || '';

  const sets = timedSets(lineup, tz);
  const picked = new Set((votes ?? []).map((v) => voteKey(v.artist_id, v.performance_day)));
  let mine = sets.filter((s) => picked.has(voteKey(s.row.artist_id, s.row.performance_day)));
  // A show with no picks: every act on the bill is yours.
  if (!festival && mine.length === 0) mine = sets;

  const onNow = mine.filter((s) => s.startMs <= now && now < s.endMs).pop() ?? null;
  const upcoming = mine.find((s) => s.startMs > now) ?? null;
  const startedToday = mine.some((s) => s.startMs <= now && now - s.startMs < 18 * HOUR_MS);

  // a show always has a picture (the island's left side): the act's, else the headliner's or the event's
  const showImage = festival ? null : showImageUrl(event, lineup);
  let nowInfo = onNow ? setInfo('NOW', onNow, venueName, showImage) : null;
  let nextInfo = upcoming
    ? setInfo(festival ? (startedToday ? 'NEXT' : 'FIRST') : 'SHOW', upcoming, venueName, showImage)
    : null;
  let overAt = null;
  if (!festival && !nowInfo && !nextInfo) {
    // no set times: the show's own start, then on as Now until the event is over (events.ends_at)
    const start = getEventStartMs(event);
    const endsAt = getEventEndsAt(event)?.getTime() ?? null;
    const show = {
      name: resolveEventDisplayName(event) || 'Show',
      stage: venueName,
      start,
      end: null,
      imageUrl: showImage,
      image: null,
    };
    if (Number.isFinite(start) && start > now) nextInfo = { tag: 'SHOW', ...show };
    else if (Number.isFinite(start) && (endsAt == null || now < endsAt))
      nowInfo = { tag: 'NOW', ...show };
    // no "till" on the card (ends_at is often a guess), but the card still comes down on time
    if (nowInfo && endsAt != null) overAt = endsAt;
  }

  if (nextInfo) Object.assign(nextInfo, countdownLabels(nextInfo.start, now));

  // "Portola · Day 2": the festival day of the set in view (else today's), counted over the lineup's days
  let title = festival
    ? resolveEventDisplayName(event)
    : venueName || resolveEventDisplayName(event);
  if (festival) {
    const days = festivalDaysFromLineup(lineup ?? []);
    const inView = onNow ?? upcoming;
    const day = inView?.row.performance_day ?? null;
    const i = day ? days.indexOf(day) : -1;
    if (days.length > 1 && i >= 0) title = `${title} · Day ${i + 1}`;
  }

  return {
    eventId,
    overAt,
    state: {
      title: title || 'Prii',
      here: Math.max(0, here | 0),
      of: Math.max(0, of | 0),
      faces: (faces ?? []).slice(0, MAX_FACES).map((f) => ({
        initial: (String(f?.displayName || '?').trim()[0] || '?').toUpperCase(),
        ring: ringColor(f?.displayName),
        photoUrl: f?.photoUrl || null,
        image: null,
      })),
      now: nowInfo,
      next: nextInfo,
      eventUrl: `prii://events/${encodeURIComponent(eventId)}`,
      // Sets is a festival's schedule: a concert's card has Find and Maps only (Sam's pick, 2026-11-01)
      setsUrl: festival ? `prii://sets/${encodeURIComponent(rootId)}` : null,
      findUrl: 'prii://find',
      mapUrl: `prii://map/${encodeURIComponent(rootId)}`,
    },
  };
}

/**
 * When the card next needs new words without any data changing: a set starting or ending, or the countdown's next
 * whole minute (an hour, while the set is more than an hour off). Null when nothing is coming.
 */
export function nextChangeAt(state, now = Date.now()) {
  const times = [];
  if (state?.now?.end) times.push(state.now.end);
  if (state?.now && !state.now.end) times.push(state.now.start + DEFAULT_SET_MS);
  if (state?.next) {
    // the minutes shown roll over each minute (rounded up). Hours too: "8 hr 40 min" carries minutes, and refreshing
    // only on the hour left the ring's "2h" up for the whole hour before (an hour late).
    const left = state.next.start - now;
    times.push(now + (left % 60000 || 60000));
  }
  const future = times.filter((t) => t > now);
  return future.length ? Math.min(...future) : null;
}

/**
 * A card with no event behind it, for checking the native side (a simulator, a phone on a quiet day): Portola Day 2
 * shifted to `startedAt` - Swedish House Mafia on for 10 min, Four Tet in 35 - counting down to `clock`. Only a build made with VITE_LIVE_ACTIVITY_DEMO=1
 * shows it (LiveActivityDriver). VITE_LIVE_ACTIVITY_DEMO=show: a concert instead (no Now, no Sets).
 */
export function demoCardState(startedAt = Date.now(), clock = Date.now(), kind = 'festival') {
  if (kind === 'show') {
    const card = demoCardState(startedAt, clock);
    const next = {
      ...card.state.next,
      tag: 'SHOW',
      name: 'Parcels',
      stage: 'Greek Theatre',
      end: null,
    };
    return {
      ...card,
      state: { ...card.state, title: 'Greek Theatre', now: null, next, setsUrl: null },
    };
  }
  const now = startedAt;
  const min = 60 * 1000;
  const img = (id) => `https://i.scdn.co/image/${id}`;
  return {
    eventId: 'demo',
    state: {
      title: 'Portola Festival · Day 2',
      here: 4,
      of: 6,
      faces: ['Maya', 'Jordan', 'Alex', 'Riley'].map((n) => ({
        initial: n[0],
        ring: ringColor(n),
        photoUrl: null,
        image: null,
      })),
      now: {
        tag: 'NOW',
        name: 'Swedish House Mafia',
        stage: 'Pier Stage',
        start: now - 10 * min,
        end: now + 65 * min,
        imageUrl: img('ab6761610000e5eb9614fcf985ad6fc5b4a7c3de'),
        image: null,
      },
      next: {
        tag: 'NEXT',
        name: 'Four Tet',
        stage: 'Warehouse',
        start: now + 35 * min,
        end: now + 125 * min,
        imageUrl: img('ab6761610000e5eb21c0ea7fc21ab3038d111ec2'),
        image: null,
        ...countdownLabels(now + 35 * 60 * 1000, clock),
      },
      eventUrl: 'prii://events/evt_portola_2026',
      setsUrl: 'prii://sets/evt_portola_2026',
      findUrl: 'prii://find',
      mapUrl: 'prii://map/evt_portola_2026',
    },
  };
}
